// ListingFilters.tsx
import React, { useCallback, useState, useEffect } from 'react';
import { Grid, FormControl, Typography, Alert, Select, MenuItem, Slider } from '@mui/material';
import FilterChip from 'src/App/Components/Chips/FilterChip';
import { FilterVal } from 'src/interfaces/iproptypes';
import { COLOR_PRIMARY } from 'src/const';
import styles from './styles';
import FilterActionButton from './FilterActionButton';
import { roomTypesObj } from './helper';
import { useUpgradeMesageExpertFeature } from 'src/hooks/useUpgradeText';
import { COLOR_PRIMARY_2 } from 'src/colors';

interface ListingFiltersProps {
  setFilterVal: React.Dispatch<React.SetStateAction<FilterVal>>;
  filterVal: FilterVal;
  isMobileView: boolean;
  setTotalFilterApplied: React.Dispatch<React.SetStateAction<number>>;
  handleCloseMenu: () => void;
  resetFilter: (filterNamesToReset: string[]) => void;
  isUserAllowed: boolean;
  handleOnSampleClick: (plan: string) => void;
  UpgradeCTA: (closeMenu: () => void) => JSX.Element;
  FilterCoingSoonComponent: React.ReactNode | null;
}

const accomodatesMarks = [
  { value: 1, label: '1' },
  { value: 16, label: '16+' },
];

const handleChipChange = ({
  filterKey,
  value,
  filterVal,
  setFilterVal,
  allowedValues = [],
  isUserAllowed = false,
}: {
  filterKey: keyof FilterVal;
  value: string | number;
  filterVal: FilterVal;
  setFilterVal: any;
  allowedValues?: (string | number)[];
  isUserAllowed?: boolean;
}) => {
  value = Number(value) || value;

  let currentVals = [...(filterVal[filterKey] as (string | number)[])];

  if (value !== 100 && currentVals.includes(100)) {
    currentVals = currentVals.filter(v => v !== 100);
  }

  if (currentVals.includes(value)) {
    currentVals = currentVals.filter(v => v !== value);
  } else {
    currentVals.push(value);
  }

  if (value === 100) {
    currentVals = [100];
  }

  // Apply filter sync rules
  setFilterVal((prev: FilterVal) => {
    const newFilterVal = {
      ...prev,
      [filterKey]: currentVals,
    };

    // Rule 2: When bedroom filter changes to specific value
    if (filterKey === 'bedrooms' && !currentVals.includes(100)) {
      const hasOnlyPrivateSharedHotel = newFilterVal.roomType.every(type =>
        ['private_room', 'shared_room', 'hotel_room'].includes(type)
      );

      if (hasOnlyPrivateSharedHotel) {
        newFilterVal.roomType = []; // Reset to 'any'
      }
    }

    return newFilterVal;
  });
};

const BedroomFilters = ({ filterVal, setFilterVal, disabled, isUserAllowed }: any) => (
  <FormControl fullWidth>
    <Typography variant="body1" style={styles.filterTitleStyle}>
      Bedrooms
    </Typography>
    {disabled && (
      <Alert severity="warning">
        Bedroom filter can only be applied to "Entire House", "Apartment (Condo)" and "Show all
        listings".
      </Alert>
    )}
    <Grid container>
      <FilterChip
        value={100}
        label="Any"
        handleOnClick={() =>
          handleChipChange({
            filterKey: 'bedrooms',
            value: 100,
            filterVal,
            setFilterVal,
            isUserAllowed,
          })
        }
        variant={filterVal.bedrooms.includes(100) ? 'filled' : 'outlined'}
      />
      {[0, 1, 2, 3, 4, 5, '6+'].map((v, i) => (
        <FilterChip
          key={i}
          value={v}
          label={
            v === '6+' ? '6+' : typeof v === 'string' ? v[0].toUpperCase() : v === 0 ? 'Studio' : v
          }
          handleOnClick={() =>
            handleChipChange({
              filterKey: 'bedrooms',
              value: v,
              filterVal,
              setFilterVal,
              isUserAllowed,
            })
          }
          variant={
            !filterVal.bedrooms.includes(v) || filterVal.bedrooms.includes(100)
              ? 'outlined'
              : 'filled'
          }
          tooltipText={roomTypesObj[v] || undefined}
        />
      ))}
    </Grid>
  </FormControl>
);

const BathroomFilters = ({ filterVal, setFilterVal, disabled, isUserAllowed }: any) => (
  <FormControl fullWidth>
    <Typography variant="body1" style={styles.filterTitleStyle}>
      Bathrooms
    </Typography>
    {disabled && (
      <Alert severity="warning">
        Bathroom filter can only be applied to "Entire House", "Apartment (Condo)" and "Show all
        listings".
      </Alert>
    )}
    <Grid container>
      <FilterChip
        value={100}
        label="Any"
        handleOnClick={() =>
          handleChipChange({
            filterKey: 'bathrooms',
            value: 100,
            filterVal,
            setFilterVal,
            isUserAllowed,
          })
        }
        variant={filterVal.bathrooms.includes(100) ? 'filled' : 'outlined'}
      />
      {[0, 1, 2, 3, 4, 5, '6+'].map((v, i) => (
        <FilterChip
          key={i}
          value={v}
          label={v === 0 ? 'S' : v === 6 ? '6+' : v}
          handleOnClick={() =>
            handleChipChange({
              filterKey: 'bathrooms',
              value: v,
              filterVal,
              setFilterVal,
              isUserAllowed,
            })
          }
          variant={
            !filterVal.bathrooms.includes(v) || filterVal.bathrooms.includes(100)
              ? 'outlined'
              : 'filled'
          }
          tooltipText={v === 0 ? 'Shared Bathroom' : undefined}
        />
      ))}
    </Grid>
  </FormControl>
);

const AccommodatesPicker = ({
  filterVal,
  setFilterVal,
  isUserAllowed,
}: {
  filterVal: FilterVal;
  setFilterVal: any;
  isUserAllowed: boolean;
}) => {
  const accommodatesOptions = Array.from({ length: 16 }, (_, i) => i + 1);

  const handleValueChange = (type: 'min' | 'max', newValue: string | number) => {
    const value = Number(newValue);
    let currentAccommodates = [...(filterVal.accommodates as [number, number])];

    if (type === 'min') {
      currentAccommodates[0] = value;
      if (currentAccommodates[0] > currentAccommodates[1]) {
        currentAccommodates[1] = Math.min(16, Math.max(value, currentAccommodates[1]));
      }
    } else {
      // type === 'max'
      currentAccommodates[1] = value;
      if (currentAccommodates[1] < currentAccommodates[0]) {
        currentAccommodates[0] = Math.max(1, Math.min(value, currentAccommodates[0]));
      }
    }
    // Ensure min is at least 1 and max is at most 16, and min <= max
    currentAccommodates[0] = Math.max(1, currentAccommodates[0]);
    currentAccommodates[1] = Math.min(16, currentAccommodates[1]);
    if (currentAccommodates[0] > currentAccommodates[1]) {
      if (type === 'min') currentAccommodates[1] = currentAccommodates[0];
      else currentAccommodates[0] = currentAccommodates[1];
    }

    setFilterVal((prev: FilterVal) => ({
      ...prev,
      accommodates: currentAccommodates as [number, number],
    }));
  };

  return (
    <FormControl fullWidth>
      <Typography variant="body1" style={styles.filterTitleStyle}>
        Sleeps
      </Typography>
      <Grid container alignItems="center" sx={{ mt: 1 }}>
        <Grid item>
          <Select
            variant="outlined"
            size="small"
            value={filterVal.accommodates[0]}
            onChange={e => handleValueChange('min', e.target.value)}
            MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
            sx={styles.rangeButtonStyle}
          >
            {accommodatesOptions.map(opt => (
              <MenuItem key={`min-${opt}`} value={opt}>
                {opt === 16 ? '16+' : opt.toString()}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item>
          <Typography sx={{ mx: 1 }}>-</Typography>
        </Grid>
        <Grid item>
          <Select
            variant="outlined"
            size="small"
            value={filterVal.accommodates[1]}
            onChange={e => handleValueChange('max', e.target.value)}
            MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
            sx={styles.rangeButtonStyle}
          >
            {accommodatesOptions.map(opt => (
              <MenuItem key={`max-${opt}`} value={opt}>
                {opt === 16 ? '16+' : opt.toString()}
              </MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>
    </FormControl>
  );
};

const PropertyTypeFilter = ({
  filterVal,
  setFilterVal,
  isUserAllowed,
}: {
  filterVal: FilterVal;
  setFilterVal: any;
  isUserAllowed: boolean;
}) => {
  const handleRoomTypeChange = useCallback((value: string) => {
    setFilterVal((filterVal: FilterVal) => {
      let roomType = [...filterVal.roomType];

      if (roomType.includes(value)) {
        roomType = roomType.filter(data => {
          return data !== value;
        });
      } else {
        roomType.push(value);
      }

      if (value === 'all') {
        roomType = [];
      }

      // Rule 1: If property type doesn't include entire_home, condo, or all
      const hasValidPropertyType = roomType.some(type =>
        ['entire_home', 'condo', 'all'].includes(type)
      );

      return {
        ...filterVal,
        roomType: roomType,
        // Force bedroom and bathroom to 'any' if no valid property type
        bedrooms: hasValidPropertyType ? filterVal.bedrooms : [100],
        bathrooms: hasValidPropertyType ? filterVal.bathrooms : [100],
      };
    });
  }, []);

  return (
    <Grid item>
      <FormControl variant="outlined" size="small" fullWidth>
        <Typography variant="body1" style={styles.filterTitleStyle}>
          Property Type
        </Typography>
        <Grid xs={12} container justifyContent="flex-start" style={{ gap: '5px' }}>
          <FilterChip
            value={'all'}
            label={'Any'}
            handleOnClick={() => handleRoomTypeChange('all')}
            variant={filterVal.roomType.length === 0 ? 'filled' : 'outlined'}
          />
          {['entire_home', 'private_room', 'shared_room', 'hotel_room'].map((roomType, index) => (
            <FilterChip
              value={roomType}
              label={roomTypesObj[roomType]}
              handleOnClick={() => handleRoomTypeChange(roomType)}
              variant={filterVal.roomType.includes(roomType) ? 'filled' : 'outlined'}
            />
          ))}
        </Grid>
      </FormControl>
    </Grid>
  );
};

const MinimumStayFilter = ({
  filterVal,
  setFilterVal,
  isUserAllowed,
}: {
  filterVal: FilterVal;
  setFilterVal: any;
  isUserAllowed: boolean;
}) => {
  const [minimumNights, setMinimumNights] = useState<[number, number]>([1, 30]);

  const minimumNightsOptions = Array.from({ length: 30 }, (_, i) => i + 1);

  const handleValueChange = (type: 'min' | 'max', newValue: string | number) => {
    const value = Number(newValue);
    let currentMinimumNights: [number, number] = [...minimumNights];

    if (type === 'min') {
      currentMinimumNights[0] = value;
      if (currentMinimumNights[0] > currentMinimumNights[1]) {
        currentMinimumNights[1] = Math.min(30, Math.max(value, currentMinimumNights[1]));
      }
    } else {
      // type === 'max'
      currentMinimumNights[1] = value;
      if (currentMinimumNights[1] < currentMinimumNights[0]) {
        currentMinimumNights[0] = Math.max(1, Math.min(value, currentMinimumNights[0]));
      }
    }
    // Ensure min is at least 1 and max is at most 30, and min <= max
    currentMinimumNights[0] = Math.max(1, currentMinimumNights[0]);
    currentMinimumNights[1] = Math.min(30, currentMinimumNights[1]);
    if (currentMinimumNights[0] > currentMinimumNights[1]) {
      if (type === 'min') currentMinimumNights[1] = currentMinimumNights[0];
      else currentMinimumNights[0] = currentMinimumNights[1];
    }

    setMinimumNights(currentMinimumNights);

    // Immediately apply the filter
    setTimeout(() => {
      setFilterVal((prevFilterVal: FilterVal) => {
        return { ...prevFilterVal, minimum_nights: currentMinimumNights };
      });
    }, 0);
  };

  return (
    <FormControl fullWidth>
      <Typography variant="body1" style={styles.filterTitleStyle}>
        Minimum Stay
      </Typography>
      <Grid container alignItems="center" sx={{ mt: 1 }}>
        <Grid item>
          <Select
            variant="outlined"
            size="small"
            value={minimumNights[0]}
            onChange={e => handleValueChange('min', e.target.value)}
            MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
            sx={styles.rangeButtonStyle}
          >
            {minimumNightsOptions.map(opt => (
              <MenuItem key={`min-nights-${opt}`} value={opt}>
                {opt === 30 ? '30+' : opt.toString()}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item>
          <Typography sx={{ mx: 1 }}>-</Typography>
        </Grid>
        <Grid item>
          <Select
            variant="outlined"
            size="small"
            value={minimumNights[1]}
            onChange={e => handleValueChange('max', e.target.value)}
            MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
            sx={styles.rangeButtonStyle}
          >
            {minimumNightsOptions.map(opt => (
              <MenuItem key={`max-nights-${opt}`} value={opt}>
                {opt === 30 ? '30+' : opt.toString()}
              </MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>
    </FormControl>
  );
};

const ListingFilters = ({
  setFilterVal,
  filterVal,
  isMobileView,
  setTotalFilterApplied,
  handleCloseMenu,
  resetFilter,
  isUserAllowed,
  handleOnSampleClick,
  UpgradeCTA,
  FilterCoingSoonComponent,
}: ListingFiltersProps) => {
  const [isDisabled, setIsDisabled] = useState(false);

  useEffect(() => {
    let appliedCount = 0;
    // Check bedrooms: active if not 'Any' (100) and has selections
    if (!filterVal.bedrooms.includes(100) && filterVal.bedrooms.length > 0) {
      appliedCount++;
    }
    // Check bathrooms: active if not 'Any' (100) and has selections
    if (!filterVal.bathrooms.includes(100) && filterVal.bathrooms.length > 0) {
      appliedCount++;
    }
    // Check accommodates: active if min is not 1 or max is not 16 (default range might vary)
    // Assuming default is [1, 16] for accommodates
    if (filterVal.accommodates[0] !== 1 || filterVal.accommodates[1] !== 16) {
      appliedCount++;
    }
    // Check propertyType: active if not 'all' and has selections
    if (!filterVal.roomType.includes('all') && filterVal.roomType.length > 0) {
      appliedCount++;
    }
    // Check minimum_nights: active if min is not 1 or max is not 30 (default range might vary)
    // Assuming default is [1, 30] for minimum_nights and 30 means 30+
    if (filterVal.minimum_nights[0] !== 1 || filterVal.minimum_nights[1] !== 30) {
      appliedCount++;
    }
    setTotalFilterApplied(appliedCount);
  }, [filterVal, setTotalFilterApplied]);

  const isNotEntireHouseOrCondo =
    filterVal.roomType.length > 0 &&
    !filterVal.roomType.includes('entire_home') &&
    !filterVal.roomType.includes('condo');

  return (
    <Grid
      container
      direction="column"
      style={{ maxWidth: '450px', padding: '5px 20px', zIndex: 999999, position: 'relative' }}
    >
      {!isUserAllowed && UpgradeCTA(handleCloseMenu)}
      {isUserAllowed && FilterCoingSoonComponent && FilterCoingSoonComponent}
      <Grid item xs={12} sx={{ mb: 2 }}>
        <BedroomFilters
          filterVal={filterVal}
          setFilterVal={setFilterVal}
          disabled={isNotEntireHouseOrCondo}
        />
      </Grid>
      <Grid item xs={12} sx={{ mb: 2 }}>
        <BathroomFilters
          filterVal={filterVal}
          setFilterVal={setFilterVal}
          disabled={isNotEntireHouseOrCondo}
        />
      </Grid>
      <Grid item xs={12} sx={{ mb: 2 }}>
        <AccommodatesPicker
          filterVal={filterVal}
          setFilterVal={setFilterVal}
          isUserAllowed={isUserAllowed}
        />
      </Grid>
      <Grid item xs={12} sx={{ mb: 2 }}>
        <PropertyTypeFilter
          filterVal={filterVal}
          setFilterVal={setFilterVal}
          isUserAllowed={isUserAllowed}
        />
      </Grid>
      <Grid item xs={12} sx={{ mb: 2 }}>
        <MinimumStayFilter
          filterVal={filterVal}
          setFilterVal={setFilterVal}
          isUserAllowed={isUserAllowed}
        />
      </Grid>
      <Grid item xs={12} sx={{ mb: 2 }}>
        <FilterActionButton
          isUserAllowed={isUserAllowed}
          handleOnSampleClick={handleOnSampleClick}
          handleCloseMenu={handleCloseMenu}
          resetFilter={() =>
            resetFilter(['bedrooms', 'bathrooms', 'accommodates', 'roomType', 'minimum_nights'])
          }
        />
      </Grid>
    </Grid>
  );
};

export default ListingFilters;
